import React, { useContext, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Button, Tag, Typography, MenuProps } from 'antd'
import { useIntl } from 'react-intl'
import {
  PiIdentificationCard,
  PiGearSix,
  PiBatteryChargingVertical,
  PiPalette,
  PiTranslate,
  PiSignOut,
  PiSun,
  PiMoon,
  PiDesktop
} from 'react-icons/pi'

import { useAppSelector, useAppDispatch } from '../store/hooks'
import { selectAuthUserSlice } from '../store/auth-user-slice'
import { selectAuthCompanySlice } from '../store/auth-company-slice'
import { selectLocaleSlice, setLocale } from '../store/locale-slice'
import { ThemeContext, ThemeMode } from '../context/themeContext'
import { useEnergyScore } from './use-energy-score'
import { getEnergyMode } from '@vacationtracker/shared/functions/user-insights/determine-energy-level'
import { getEnergyModeNameMap, ENERGY_MODE_VISUAL_CONFIG } from '../routes/EnergyLevelPage/services/energy-mode-config'
import { availableLanguages } from '@vacationtracker/shared/i18n'
import { FrontendUrls } from '../types/urls'
import { LocaleEnum } from '@vacationtracker/shared/types/i18n'
import { HourFormatEnum } from '@vacationtracker/shared/types/user'
import { getHourFormatBasedOnTimezone } from '@vacationtracker/shared/functions/convert-between-hour-formats'
import Api from '@vacationtracker/shared/services/api'
import * as Sentry from '@sentry/react'
import { track } from '../services/analytics/analytics'

import IntlMessages from '../util/IntlMessages'

const { Text } = Typography

interface UseProfileMenuItemsProps {
  onLogout: () => void
  onTrackProfileMenuMyProfileClick?: (data: { isAdmin: boolean; destinationRoute: string }) => void
  onTrackProfileMenuAccountSettingsClick?: () => void
  onTrackProfileMenuLogoutClick?: () => void
}

interface UseProfileMenuItemsReturn {
  profileMenuItems: MenuProps['items']
  energyScore: number
  shouldShowEnergyScore: boolean
  isEnergyFeatureEnabled: boolean
}

export const useProfileMenuItems = ({
  onLogout,
  onTrackProfileMenuMyProfileClick,
  onTrackProfileMenuAccountSettingsClick,
  onTrackProfileMenuLogoutClick,
}: UseProfileMenuItemsProps): UseProfileMenuItemsReturn => {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { locale } = useAppSelector(selectLocaleSlice)
  const { themeMode, setThemeMode } = useContext(ThemeContext)

  // Energy score hook
  const { energyScore, shouldShowEnergyScore, isFeatureEnabled: isEnergyFeatureEnabled } = useEnergyScore({
    userId: authUser?.id,
    currentUserId: authUser?.id,
    companyId: authCompany?.id,
  })

  // Get role display text
  const getRoleDisplayText = () => {
    if (authUser.role === 'Admin') {
      return formatMessage({ id: 'app.role.administrator' })
    }
    if (authUser.role === 'Approver') {
      return formatMessage({ id: 'app.role.approver' })
    }
    return formatMessage({ id: 'app.user' })
  }

  // Get theme mode label
  const getThemeModeLabel = () => {
    switch (themeMode) {
      case 'light':
        return formatMessage({ id: 'sidebar.userMenu.theme.light' })
      case 'dark':
        return formatMessage({ id: 'sidebar.userMenu.theme.dark' })
      default:
        return formatMessage({ id: 'sidebar.userMenu.theme.system' })
    }
  }

  // Calculate energy mode info
  const energyModeInfo = useMemo(() => {
    if (!shouldShowEnergyScore || energyScore === 0) return null
    const mode = getEnergyMode(energyScore)
    const nameMap = getEnergyModeNameMap(formatMessage)
    const visualConfig = ENERGY_MODE_VISUAL_CONFIG[mode]
    return {
      name: nameMap[mode],
      color: visualConfig?.primaryColor || '#000',
    }
  }, [energyScore, shouldShowEnergyScore, formatMessage])

  // Handle language change
  const handleLanguageChange = async (langLocale: LocaleEnum) => {
    dispatch(setLocale(availableLanguages[langLocale]))
    const hourFormat = authUser.hourFormat || getHourFormatBasedOnTimezone(authUser.location?.timezone) || HourFormatEnum.twentyFour
    track('USER_CHANGED_LANGUAGE', {
      language: availableLanguages[langLocale].name,
    })
    
    try {
      const body = authUser.role === 'Admin'
        ? {
          eventType: 'USER_UPDATED',
          eventGroup: 'USER',
          userId: authUser.id,
          name: authUser.name,
          locale: langLocale,
          email: authUser.email,
          platform: authUser.platform,
          startDate: authUser.startDate,
          isAdmin: authUser.role === 'Admin',
          isNameLocked: authUser?.isNameLocked,
          status: authUser.status,
          hourFormat,
        }
        : {
          eventType: 'USER_UPDATED',
          eventGroup: 'USER',
          userId: authUser.id,
          name: authUser.name,
          locale: langLocale,
          hourFormat,
        }
      await Api.post('/core/event', body)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  // Handle theme change
  const handleThemeChange = (mode: ThemeMode) => {
    setThemeMode(mode)
    track('USER_CHANGED_THEME', {
      themeMode: mode,
    })
  }

  // Filter available languages based on feature flags
  const availableLanguageOptions = useMemo(() => {
    return Object.values(availableLanguages)
  }, [])

  // Build profile menu items
  const profileMenuItems: MenuProps['items'] = useMemo(() => {
    const items: MenuProps['items'] = [
      // Header section (non-clickable)
      {
        key: 'header',
        type: 'group',
        label: (
          <div className="profile-menu-header">
            <Text className="profile-menu-header-name" strong>{authUser.name}</Text>
            <div className="profile-menu-header-info">
              <Text className="profile-menu-header-company" type="secondary">{authCompany?.name}</Text>
              <Tag className="profile-menu-header-role">{getRoleDisplayText()}</Tag>
            </div>
          </div>
        ),
      },
      { type: 'divider' },
      // My Profile
      {
        key: 'my-profile',
        icon: <PiIdentificationCard size={18} />,
        label: (
          <Link to={authUser.role === 'Admin' ? `/app/users/${authUser.id}` : '/app/my-profile'}>
            <IntlMessages id="app.myProfile" />
          </Link>
        ),
        onClick: () => {
          const destinationRoute = authUser.role === 'Admin' 
            ? `/app/users/${authUser.id}` 
            : '/app/my-profile'
          onTrackProfileMenuMyProfileClick?.({
            isAdmin: authUser.role === 'Admin',
            destinationRoute,
          })
        },
      },
      // Account Settings
      {
        key: 'account-settings',
        icon: <PiGearSix size={18} />,
        label: (
          <Link to={FrontendUrls.accountSettings}>
            <IntlMessages id="app.accountSettings" />
          </Link>
        ),
        onClick: () => {
          onTrackProfileMenuAccountSettingsClick?.()
        },
      },
    ]

    // Energy Level (only if feature is enabled and user has energy score)
    if (shouldShowEnergyScore && energyModeInfo) {
      items.push(
        { type: 'divider' },
        {
          key: 'energy-level',
          icon: <PiBatteryChargingVertical size={18} />,
          label: (
            <Link to={FrontendUrls.energyLevel} className="profile-menu-energy-link">
              <span className="profile-menu-energy-label">
                <IntlMessages id="app.myEnergyLevel" />
              </span>
              <span className="profile-menu-energy-info">
                <span className="profile-menu-energy-current">
                  <IntlMessages id="sidebar.userMenu.currentLevel" />
                </span>
                <span
                  className="profile-menu-energy-indicator"
                  style={{ color: energyModeInfo.color }}
                >
                  <span
                    className="profile-menu-energy-dot"
                    style={{ backgroundColor: energyModeInfo.color }}
                  />
                  {energyModeInfo.name}
                </span>
              </span>
            </Link>
          ),
        }
      )
    }

    // Preferences section
    items.push(
      { type: 'divider' },
      // Color theme submenu
      {
        key: 'color-theme',
        icon: <PiPalette size={18} />,
        label: (
          <span className="profile-menu-preference-label">
            <IntlMessages id="sidebar.userMenu.colorTheme" />
            <span className="profile-menu-preference-value">{getThemeModeLabel()}</span>
          </span>
        ),
        children: [
          {
            key: 'theme-light',
            icon: <PiSun size={16} />,
            label: <IntlMessages id="sidebar.userMenu.theme.light" />,
            className: themeMode === 'light' ? 'profile-menu-submenu-selected' : undefined,
            onClick: () => handleThemeChange('light'),
          },
          {
            key: 'theme-dark',
            icon: <PiMoon size={16} />,
            label: <IntlMessages id="sidebar.userMenu.theme.dark" />,
            className: themeMode === 'dark' ? 'profile-menu-submenu-selected' : undefined,
            onClick: () => handleThemeChange('dark'),
          },
          {
            key: 'theme-system',
            icon: <PiDesktop size={16} />,
            label: <IntlMessages id="sidebar.userMenu.theme.system" />,
            className: themeMode === 'system' ? 'profile-menu-submenu-selected' : undefined,
            onClick: () => handleThemeChange('system'),
          },
        ],
      },
      // Language submenu
      {
        key: 'language',
        icon: <PiTranslate size={18} />,
        label: (
          <span className="profile-menu-preference-label">
            <IntlMessages id="sidebar.userMenu.language" />
            <span className="profile-menu-preference-value">{locale.name}</span>
          </span>
        ),
        children: availableLanguageOptions.map(lang => ({
          key: `lang-${lang.locale}`,
          label: lang.name,
          className: locale.locale === lang.locale ? 'profile-menu-submenu-selected' : undefined,
          onClick: () => handleLanguageChange(lang.locale),
        })),
      },
      { type: 'divider' },
      // Logout
      {
        key: 'logout',
        icon: <PiSignOut size={18} />,
        danger: true,
        label: (
          <Button
            onClick={() => {
              onTrackProfileMenuLogoutClick?.()
              onLogout()
            }}
            className="profile-menu-logout-button"
            type="link"
            danger
          >
            <IntlMessages id="app.logout" />
          </Button>
        ),
      }
    )

    return items
  }, [
    authUser,
    authCompany,
    locale,
    themeMode,
    shouldShowEnergyScore,
    energyModeInfo,
    availableLanguageOptions,
    formatMessage,
    onLogout,
    onTrackProfileMenuMyProfileClick,
    onTrackProfileMenuAccountSettingsClick,
    onTrackProfileMenuLogoutClick,
  ])

  return {
    profileMenuItems,
    energyScore,
    shouldShowEnergyScore,
    isEnergyFeatureEnabled,
  }
}

